import React, { useMemo } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { simulateExpressions } from '../EmotionBars/EmotionBars';
import './EmotionChart.css';

/**
 * EmotionChart — Compact analytics panel showing emotion distribution
 * across all currently displayed news articles.
 *
 * Props:
 *   articles:        array of news article objects (from NewsAPI)
 *   detectedEmotion: string — the dominant emotion detected (e.g. 'happy')
 *   title:           string — optional section title
 */

const EMOTIONS = [
  { key: 'happy',     label: 'Joy',      emoji: '😊', color: '#f59e0b' },
  { key: 'angry',     label: 'Anger',    emoji: '😡', color: '#ef4444' },
  { key: 'sad',       label: 'Sadness',  emoji: '😢', color: '#3b82f6' },
  { key: 'fearful',   label: 'Fear',     emoji: '😨', color: '#8b5cf6' },
  { key: 'surprised', label: 'Surprise', emoji: '😲', color: '#06b6d4' },
];

const SENTIMENT_SCORE = { happy: 1, surprised: 0.5, fearful: -0.2, sad: -0.7, angry: -1 };

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const d = payload[0];
    return (
      <div className="echart-tooltip">
        <span>{d.payload.emoji} {d.name}</span>
        <strong>{d.value.toFixed(1)}%</strong>
      </div>
    );
  }
  return null;
};

const EmotionChart = ({ articles = [], detectedEmotion = 'happy', title = 'Emotion Analytics' }) => {
  // Aggregate simulated expressions across all articles
  const { chartData, totals, avgSentiment, mostCommon } = useMemo(() => {
    if (!articles.length) return { chartData: [], totals: {}, avgSentiment: 0, mostCommon: null };

    const sums = { happy: 0, angry: 0, sad: 0, fearful: 0, surprised: 0 };

    articles.forEach((_, idx) => {
      const expr = simulateExpressions(detectedEmotion, idx + 1);
      EMOTIONS.forEach(e => {
        sums[e.key] += (expr[e.key] || 0);
      });
    });

    // Average across articles
    const avgs = {};
    EMOTIONS.forEach(e => { avgs[e.key] = sums[e.key] / articles.length; });

    const total = Object.values(avgs).reduce((a, b) => a + b, 0);
    const chartData = EMOTIONS.map(e => ({
      name: e.label,
      emoji: e.emoji,
      value: parseFloat(((avgs[e.key] / total) * 100).toFixed(1)),
      color: e.color,
    }));

    // Average sentiment score (-1 to +1)
    let sentSum = 0;
    EMOTIONS.forEach(e => { sentSum += avgs[e.key] * (SENTIMENT_SCORE[e.key] || 0); });
    const avgSentiment = sentSum / total;

    // Most common emotion
    const mostCommon = EMOTIONS.reduce((best, e) =>
      avgs[e.key] > (avgs[best.key] || 0) ? e : best
    , EMOTIONS[0]);

    return { chartData, totals: avgs, avgSentiment, mostCommon };
  }, [articles, detectedEmotion]);

  if (!articles.length) return null;

  const sentimentLabel = avgSentiment > 0.3 ? 'Positive' : avgSentiment < -0.3 ? 'Negative' : 'Neutral';
  const sentimentClass = avgSentiment > 0.3 ? 'positive' : avgSentiment < -0.3 ? 'negative' : 'neutral';

  return (
    <div className="echart-card animate-fade-in" role="region" aria-label={title}>
      <div className="echart-header">
        <h3 className="echart-title">{title}</h3>
        <span className="echart-badge">Live</span>
      </div>

      {/* Stats Row */}
      <div className="echart-stats">
        <div className="echart-stat">
          <span className="echart-stat__value">{articles.length}</span>
          <span className="echart-stat__label">Articles</span>
        </div>
        <div className="echart-stat">
          <span className={`echart-stat__value echart-stat__value--${sentimentClass}`}>{sentimentLabel}</span>
          <span className="echart-stat__label">Sentiment</span>
        </div>
        <div className="echart-stat">
          <span className="echart-stat__value">{mostCommon?.emoji} {mostCommon?.label}</span>
          <span className="echart-stat__label">Top Emotion</span>
        </div>
      </div>

      {/* Pie Chart */}
      <div className="echart-chart-wrapper">
        <ResponsiveContainer width="100%" height={160}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={48}
              outerRadius={72}
              paddingAngle={3}
              dataKey="value"
              animationBegin={100}
              animationDuration={800}
            >
              {chartData.map((entry, idx) => (
                <Cell key={entry.name} fill={entry.color} stroke="none" />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>

        {/* Center label */}
        <div className="echart-center-label" aria-hidden>
          <span className="echart-center-emoji">{mostCommon?.emoji}</span>
          <span className="echart-center-text">{mostCommon?.label}</span>
        </div>
      </div>

      {/* Legend */}
      <div className="echart-legend">
        {chartData.map(d => (
          <div key={d.name} className="echart-legend-item">
            <span className="echart-legend-dot" style={{ background: d.color }} />
            <span className="echart-legend-emoji">{d.emoji}</span>
            <span className="echart-legend-name">{d.name}</span>
            <span className="echart-legend-val">{d.value}%</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EmotionChart;
